import { HomeOutlined, LogoutOutlined } from '@mui/icons-material';
import {
	AppBar,
	Box,
	Button,
	Fade,
	Stack,
	Typography,
	useTheme
} from '@mui/material';
import { useAppConfig } from 'api/appConfig';
import { useLogout } from 'api/auth';
import { useSecondaryConfigName, useTenantName } from 'api/configurations';
import CleanLink from 'components/shared/CleanLink';
import SupportEmailTooltip from 'components/shared/SupportEmailTooltip';
import ThemeModeButton from 'components/shared/ThemeModeButton';
import { ROUTES } from 'constants/routes';
import { useAuthContext } from 'contexts/AuthContext';
import { useModeTheme } from 'contexts/ModeThemeContext';
import React from 'react';
import { AuthSource, UnauthorizedReason } from 'types/api';

const AppTopBar: React.FC = () => {
	const {
		assets,
		palette: { mainPaperBackground }
	} = useTheme();

	const { data: tenantName } = useTenantName();
	const { data: secondaryConfigName } = useSecondaryConfigName();
	const { themeLoaded } = useModeTheme();
	const { data: appConfig } = useAppConfig();
	const { authRes, setAuthRes } = useAuthContext();
	const { mutate: logout, isLoading: loggingOut } = useLogout(() =>
		setAuthRes((prev) => ({
			...prev,
			verified: false,
			authSource: null,
			unauthorizedReason: UnauthorizedReason.NO_AUTH
		}))
	);
	return (
		<AppBar
			position="static"
			sx={{ bgcolor: mainPaperBackground }}
			elevation={0}
		>
			<Fade in={themeLoaded}>
				<Stack
					direction="row"
					height={64}
					alignItems="center"
					px={4}
					justifyContent="space-between"
				>
					<Stack direction="row" alignItems="center" gap={1} height="100%">
						<CleanLink
							data-testid="home-link"
							to={ROUTES.home}
							style={{ height: '100%' }}
						>
							<Stack
								direction="row"
								gap={1}
								sx={{ height: '100%' }}
								alignItems="center"
								py={1}
							>
								{assets?.logo ? (
									<Box
										component="img"
										src={assets?.logo}
										alt="Logo"
										height="100%"
										data-testid="topbar-logo"
									/>
								) : (
									<HomeOutlined data-testid="topbar-home-icon" />
								)}
								<Typography variant="h3" flexShrink={0}>
									{intl.tr('app.navbar.title')}
								</Typography>
							</Stack>
						</CleanLink>
						{tenantName && (
							<>
								<Typography variant="h3">/</Typography>
								<Typography variant="h3" data-testid="topbar-tenant-name">
									{tenantName}
								</Typography>
							</>
						)}
						{secondaryConfigName && (
							<>
								<Typography variant="h3">/</Typography>
								<Typography variant="h3" data-testid="topbar-sso-name">
									{secondaryConfigName}
								</Typography>
							</>
						)}
					</Stack>
					<Stack direction="row" alignItems="center" gap={1}>
						{appConfig?.supportEmail && (
							<SupportEmailTooltip supportEmail={appConfig.supportEmail} />
						)}
						<ThemeModeButton />
						{authRes.authSource === AuthSource.DSRSSO && (
							<Button
								data-testid="topbar-logout"
								startIcon={<LogoutOutlined />}
								disabled={loggingOut}
								onClick={() => logout()}
							>
								{intl.tr('app.navbar.logout')}
							</Button>
						)}
					</Stack>
				</Stack>
			</Fade>
		</AppBar>
	);
};

export default AppTopBar;
